import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, Search, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchCtoMembers,
  formatDate,
  formatToolName,
  formatWhen,
  memberState,
  type CtoMemberRow,
} from '../../lib/ctoMembers';
import { ClientIcon } from './ClientIcon';
import { surfaceForClient } from '../../lib/mcpClientIcons';

/**
 * Who is on My AI CTO and what they do with it.
 *
 * The list is activations, not subscribers. Someone on the newsletter with no
 * activation has no access — cto-mcp refuses them a token — so showing them here would
 * misrepresent the size of the service. Connections at /subscribers is the CRM list;
 * this is the membership.
 */
export default function CtoMembersView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CtoMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchCtoMembers());
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load CTO members');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        r.tierName.toLowerCase().includes(q) ||
        r.clients.some((c) => c.toLowerCase().includes(q)),
    );
  }, [rows, searchQuery]);

  // Counted off every activation, not the filtered view — the summary describes the
  // service, and would otherwise change as you type in the search box.
  const stats = useMemo(() => {
    const live = rows.filter((r) => !r.revokedAt);
    return {
      activated: live.length,
      connected: live.filter((r) => r.clients.length > 0).length,
      active: live.filter((r) => r.callsLast7Days > 0).length,
      paid: live.filter((r) => r.paid).length,
      calls7d: rows.reduce((sum, r) => sum + r.callsLast7Days, 0),
    };
  }, [rows]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <UserCheck className="h-7 w-7 text-emerald-500" />
            Members
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Everyone who has activated My AI CTO, and what they have asked it for. Activation
            is the entitlement — subscribers who never activated have no access and are not
            listed here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh"
          className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!loading && rows.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          <Stat label="Activated" value={stats.activated} />
          <Stat label="Connected" value={stats.connected} />
          <Stat label="Active 7d" value={stats.active} tone="emerald" />
          <Stat label="Paid" value={stats.paid} />
          <Stat label="Calls 7d" value={stats.calls7d} />
        </div>
      ) : null}

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          placeholder="Search by name, email, tier, or assistant…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          Nobody has activated My AI CTO yet. Activation happens at
          members.stephansmith.me/setup.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No members match that search.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">State</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Activated</th>
                <th className="px-4 py-3">Assistants</th>
                <th className="px-4 py-3">Last tool</th>
                <th className="px-4 py-3 text-right">Month</th>
                <th className="px-4 py-3 text-right">7d</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((row) => (
                <MemberRow
                  key={row.subscriberId}
                  row={row}
                  onOpen={() => navigate(`/cto/members/${row.subscriberId}`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberRow({ row, onOpen }: { row: CtoMemberRow; onOpen: () => void }) {
  const state = memberState(row);

  return (
    <tr
      onClick={onOpen}
      className="bg-white dark:bg-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
    >
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 dark:text-gray-100">{row.name ?? row.email}</div>
        {row.name ? (
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{row.email}</div>
        ) : null}
      </td>

      <td className="px-4 py-3">
        <StateBadge label={state.label} tone={state.tone} />
        {/* An activation that never reached the automation got no onboarding email. It is
            fixable by hand, so it has to be visible. */}
        {!row.revokedAt && !row.automationEnrolledAt ? (
          <div
            className="mt-1 text-[11px] text-amber-700 dark:text-amber-300"
            title="activate_mcp could not enroll them in cto-mcp-activation. They received no onboarding sequence."
          >
            No onboarding
          </div>
        ) : null}
      </td>

      <td className="px-4 py-3">
        <div className="text-gray-900 dark:text-gray-100">{row.tierName}</div>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {row.paid ? row.membershipStatus : row.membershipStatus ? `${row.membershipStatus} · no access` : 'Free'}
          {/* A test-mode row grants nothing in production; without this the tier column
              would claim otherwise. */}
          {row.livemode === false ? ' · test mode' : ''}
        </div>
      </td>

      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {formatDate(row.activatedAt)}
      </td>

      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
        {row.clients.length > 0 ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            {row.clients.map((name) => (
              <span key={name} className="inline-flex items-center gap-1">
                <ClientIcon clientName={name} />
                <span>{surfaceForClient(name)?.name ?? name}</span>
              </span>
            ))}
          </span>
        ) : (
          '—'
        )}
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-gray-900 dark:text-gray-100">
          {formatToolName(row.lastToolName)}
          {row.lastToolOutcome === 'error' ? (
            <span className="ml-1.5 text-xs text-rose-600 dark:text-rose-400">failed</span>
          ) : null}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {formatWhen(row.lastToolAt)}
        </div>
      </td>

      <td className="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-gray-100">
        {row.usageUnlimited || row.monthlyCallLimit == null
          ? `${row.callsThisMonth.toLocaleString()} / ∞`
          : `${row.callsThisMonth.toLocaleString()} / ${row.monthlyCallLimit.toLocaleString()}`}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-gray-100">
        {row.callsLast7Days.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-gray-600 dark:text-gray-400">
        {row.totalCalls.toLocaleString()}
      </td>
    </tr>
  );
}

const STATE_SKIN: Record<string, string> = {
  good: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  idle: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  off: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
};

function StateBadge({ label, tone }: { label: string; tone: string }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATE_SKIN[tone] ?? STATE_SKIN.idle}`}
    >
      {label}
    </span>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'emerald';
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          tone === 'emerald'
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}
